import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { RootState } from "../../../utils/store";
import { ChatList } from "./ChatList";
import { ChatMessageForm } from "./ChatMessageForm";
import { ChatRequestTaskQue } from "../../../api/chatRequestTaskQueue";
import { fixLongName } from "../../../products/copy-assistant/utils";
import { ChatMessageList } from "./ChatMessageList";
import { useSelector } from "react-redux";
import { Dropdown } from "../../../components/tailwind/dropdown/Dropdown";
import {
  useCreateChatMutation,
  useCreateMessageMutation,
  useDeleteChatMutation,
} from "../../../api/chatApi";
import type { Option } from "../../../components/tailwind/dropdown/Dropdown";
import type {
  Chat,
  MessageInteractionPhase,
  Message,
  ModelOption,
} from "./types";

type ChatLayoutProps = {
  chats: string; // JSON string
  modelOptions: string; // JSON string
};

const parseChats = (json: string): Chat[] => {
  const chats = JSON.parse(json || "[]") as Chat[];
  return chats.map((c) => ({ ...c, id: Number(c.id) }));
};

const getInitialChatId = (parsed: Chat[]): number | null => {
  const urlParams = new URLSearchParams(window.location.search);
  const chatParam = parseInt(urlParams.get("id") ?? "", 10);
  if (Number.isFinite(chatParam) && parsed.some((c) => c.id === chatParam)) {
    return chatParam;
  }
  return parsed.length > 0 ? parsed[0].id : null;
};

export const ChatLayout: React.FC<ChatLayoutProps> = ({
  chats: chatList,
  modelOptions,
}) => {
  const parsedModelOptions: ModelOption[] = JSON.parse(modelOptions || "[]");

  const token = useSelector((state: RootState) => state.auth.token);

  const [createMessage] = useCreateMessageMutation();
  const [deleteChatRequest] = useDeleteChatMutation();
  const [
    createChatRequest,
    { isLoading: isCreatingChat },
  ] = useCreateChatMutation();

  const [chats, setChats] = useState<Chat[]>(() => parseChats(chatList));
  const [messageStep, setMessageStep] = useState<number>(0); // Used to animate non-streaming-responses
  const [assistantStreamVisible, setAssistantStreamVisible] = useState(false);
  const [selectedModel, setSelectedModel] = useState<ModelOption>();
  const [activeChatId, setActiveChatId] = useState<number | null>(() =>
    getInitialChatId(parseChats(chatList))
  );
  const [interactionPhase, setInteractionPhase] = useState<
    MessageInteractionPhase
  >("idle");

  const messageScrollAreaRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const scrollRef = messageScrollAreaRef.current;
    if (!scrollRef) {
      return;
    }
    scrollRef.scrollTop = scrollRef.scrollHeight;
  }, [activeChatId]);

  useEffect(() => {
    if (activeChatId != null) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set("id", activeChatId.toString());
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${urlParams}`
      );
    }
  }, [activeChatId]);

  useEffect(() => {
    setInteractionPhase("idle");
    setSelectedModel(getInitialModel());
  }, [activeChatId]);

  useEffect(() => {
    if (interactionPhase === "creating message") {
      setAssistantStreamVisible(false);
    }
  }, [interactionPhase]);

  const activeChat = useMemo((): Chat | null => {
    if (activeChatId == null) {
      return null;
    }
    return chats.find((c) => c.id === activeChatId) ?? null;
  }, [chats, activeChatId]);

  const chatRequestTaskQue = useMemo(() => {
    if (token) {
      return new ChatRequestTaskQue(token);
    }
  }, [token]);

  const modelDropdownOptions: Option<string>[] = useMemo(
    () =>
      parsedModelOptions.map((item) => ({
        key: item.model_name,
        value: item.model_name,
        text: item.display_name,
      })),
    []
  );

  const chatDropdownOptions = useMemo(
    () => [
      {
        key: "__new_chat__",
        value: -1,
        text: "+ New chat",
      },
      ...chats.map((chat) => ({
        key: String(chat.id),
        value: Number(chat.id),
        text: chat.messages[0]
          ? fixLongName(chat.messages[0].text, 24)
          : "Unnamed chat",
      })),
    ],
    [chats]
  );

  const getInitialModel = (): ModelOption | null => {
    return activeChat
      ? parsedModelOptions.find(
          (item) => item.model_name == activeChat.modelName
        )
      : null;
  };

  const handleChatSelect = (chatId: number): void => {
    setActiveChatId(chatId);
    setMessageStep(0);
  };

  const handleModelSelect = (modelName: string): void => {
    setSelectedModel(
      parsedModelOptions.find((item) => item.model_name === modelName)
    );
  };

  const handleChatCreate = async (): Promise<void> => {
    if (isCreatingChat) {
      return;
    }

    const emptyChat = chats.find((chat) => chat.messages.length === 0);
    if (emptyChat) {
      setActiveChatId(emptyChat.id);
      setMessageStep(0);
      return;
    }

    try {
      const response = await createChatRequest({}).unwrap();

      setChats([
        {
          id: response.id,
          modelName: response.model_name,
          messages: [],
        },
        ...chats,
      ]);
      setActiveChatId(response.id);
      setMessageStep(0);
    } catch (error) {
      console.error(error);
    }
  };

  const handleChatDelete = async (chatId: number): Promise<void> => {
    if (isCreatingChat) {
      return;
    }

    setChats((l) => l.filter((item) => item.id != chatId));

    try {
      await deleteChatRequest({
        chat_id: chatId,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleMessageAdded = (newMessage: Message): void => {
    setChats((prevChats) =>
      prevChats.map((chat) =>
        chat.id === activeChatId
          ? { ...chat, messages: [...chat.messages, newMessage] }
          : chat
      )
    );
  };

  const handleAssistantStreamUpdate = useCallback(
    (assistant: Message): void => {
      setAssistantStreamVisible(true);
      setChats((prev) =>
        prev.map((chat) =>
          chat.id === activeChatId
            ? {
                ...chat,
                messages: (() => {
                  const next = [...chat.messages];
                  const last = next[next.length - 1];
                  if (last?.role === "assistant") {
                    next[next.length - 1] = assistant;
                  } else {
                    next.push(assistant);
                  }
                  return next;
                })(),
              }
            : chat
        )
      );
    },
    [activeChatId]
  );

  const handleNonStreamResponse = (): void => setMessageStep(1);

  return (
    <article
      className={[
        "txu-form",
        "tw-w-[max(100%,100vw)]",
        "tw-grid",
        "tw-grid-cols-[auto_1fr]",
        "tw-h-full-min-header",
        "tw-mx-auto",
      ].join(" ")}
    >
      <ChatList
        className={[
          "tw-hidden",
          "md:tw-block",
          "tw-col-start-1",
          "tw-h-full",
          "tw-p-4",
        ].join(" ")}
        summaries={chats.map((chat) => ({
          name: chat.messages[0]
            ? fixLongName(chat.messages[0].text, 24)
            : "Unnamed chat",
          id: chat.id,
          timeSinceActive: chat.messages[chat.messages.length - 1]?.timeStamp,
        }))}
        onChatSelect={handleChatSelect}
        onChatCreate={handleChatCreate}
        onChatDelete={handleChatDelete}
        activeChatId={activeChatId || undefined}
      />

      <div
        className={[
          "tw-col-start-2",
          "tw-flex",
          "tw-h-full",
          "tw-min-h-0",
          "tw-flex-col",
          "tw-overflow-hidden",
          "tw-border-l",
          "tw-p-6",
        ].join(" ")}
      >
        <header className="tw-flex tw-gap-2 tw-flex-none">
          <div className="tw-block md:tw-hidden">
            <Dropdown
              variant="alternate"
              options={chatDropdownOptions}
              value={activeChatId || undefined}
              placeholder="Select chat"
              onChange={(next): void => {
                if (next === undefined || next === null) return;
                if (next === -1) {
                  void handleChatCreate();
                  return;
                }
                const id = Number(next);
                handleChatSelect(id);
              }}
            />
          </div>
          <Dropdown
            options={modelDropdownOptions}
            value={selectedModel?.model_name || null}
            onChange={handleModelSelect}
          />
        </header>

        <div
          className={[
            "tw-w-full",
            "tw-py-6",
            "tw-min-h-0",
            "tw-flex",
            "tw-flex-1",
            "tw-flex-col",
            "tw-items-center",
            "tw-justify-between",
            "tw-overflow-hidden",
          ].join(" ")}
        >
          {activeChat ? (
            <>
              <section
                ref={messageScrollAreaRef}
                className={[
                  "tw-w-full",
                  "tw-min-h-0",
                  "tw-flex",
                  "tw-flex-1",
                  "tw-justify-center",
                  "tw-overflow-y-auto",
                ].join(" ")}
                style={{ scrollbarGutter: "stable" }}
              >
                <div
                  className={[
                    "tw-w-full",
                    "tw-max-w-[1450px]",
                    "tw-min-h-0",
                    "tw-pr-2",
                    "tw-grow",
                  ].join(" ")}
                >
                  <ChatMessageList
                    key={activeChatId}
                    messages={activeChat.messages}
                    messageStep={messageStep}
                    assistantReplyStreaming={
                      interactionPhase === "awaiting reply"
                    }
                  />

                  {/* loading animation */}
                  {interactionPhase === "awaiting reply" &&
                    !assistantStreamVisible && (
                      <div className="tw-ml-4 tw-flex tw-gap-2">
                        <i
                          className={[
                            "tw-w-3",
                            "tw-h-3",
                            "tw-rounded-full",
                            "tw-bg-gray-400",
                            "tw-transition",
                            "tw-animate-pulse",
                          ].join(" ")}
                          style={{ animationDuration: "1s" }}
                        />
                        <i
                          className={[
                            "tw-w-3",
                            "tw-h-3",
                            "tw-rounded-full",
                            "tw-bg-gray-400",
                            "tw-transition",
                            "tw-animate-pulse",
                          ].join(" ")}
                          style={{
                            animationDuration: "1s",
                            animationDelay: "100ms",
                          }}
                        />
                        <i
                          className={[
                            "tw-w-3",
                            "tw-h-3",
                            "tw-rounded-full",
                            "tw-bg-gray-400",
                            "tw-transition",
                            "tw-animate-pulse",
                          ].join(" ")}
                          style={{
                            animationDuration: "1s",
                            animationDelay: "200ms",
                          }}
                        />
                      </div>
                    )}
                </div>
              </section>

              <ChatMessageForm
                chatId={activeChatId}
                selectedModel={selectedModel}
                chatRequestTaskQue={chatRequestTaskQue}
                interactionPhase={interactionPhase}
                createMessage={createMessage}
                setResponsePhase={setInteractionPhase}
                onMessageAdded={handleMessageAdded}
                onAssistantStreamUpdate={handleAssistantStreamUpdate}
                onNonStreamResponse={handleNonStreamResponse}
              />
            </>
          ) : (
            <p>No chat selected. Please select a chat or create a new one.</p>
          )}
        </div>
      </div>
    </article>
  );
};
